
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { UserTimeEntriesHeader } from "./time-tracking/user-entries/UserTimeEntriesHeader";
import { UserTimeEntriesState } from "./time-tracking/user-entries/UserTimeEntriesState";
import { TimeEntriesTable } from "./TimeEntriesTable";
import { useUserTimeEntries } from "@/hooks/useUserTimeEntries";
import { TimeEntryFilter } from "./time-tracking/TimeEntryFilter";
import { useAvailableMonths } from "@/hooks/useAvailableMonths";
import { useState } from "react";
import { filterTimeEntriesByMonth } from "@/utils/dateFilters";

interface UserTimeEntriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
}

export function UserTimeEntriesDialog({
  open,
  onOpenChange,
  userId,
  userEmail,
}: UserTimeEntriesDialogProps) {
  const [selectedMonth, setSelectedMonth] = useState("current");
  const { timeEntries, isLoading } = useUserTimeEntries(userId, open);
  const availableMonths = useAvailableMonths(userId);

  // Use centralized filtering logic (SSOT)
  const filteredTimeEntries = filterTimeEntriesByMonth(timeEntries, selectedMonth);

  // Calculate total duration for filtered entries (duration is in seconds)
  const totalDuration = filteredTimeEntries.reduce((total, entry) => {
    return total + (entry.duration || 0);
  }, 0);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <UserTimeEntriesHeader 
          userEmail={userEmail} 
          timeEntries={filteredTimeEntries} 
        />
        
        {!isLoading && timeEntries?.length ? (
          <TimeEntryFilter
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            availableMonths={availableMonths}
            totalDuration={totalDuration}
          />
        ) : null}
        
        <UserTimeEntriesState 
          isLoading={isLoading} 
          hasEntries={Boolean(filteredTimeEntries?.length)}
          totalEntries={timeEntries?.length}
          filteredEntries={filteredTimeEntries?.length}
          selectedMonth={selectedMonth}
        />
        
        {!isLoading && filteredTimeEntries.length > 0 ? (
          <TimeEntriesTable timeEntries={filteredTimeEntries} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
